import { FC, createContext, useMemo, PropsWithChildren } from 'react'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import {
  ShortCodeContextProvider_cemeteryFragment$data,
  ShortCodeContextProvider_cemeteryFragment$key
} from './__generated__/ShortCodeContextProvider_cemeteryFragment.graphql'
import {
  ShortCodeContextProvider_postFragment$data,
  ShortCodeContextProvider_postFragment$key
} from './__generated__/ShortCodeContextProvider_postFragment.graphql'

type ShortCodeContextPosts = {
  [slug: string]: ShortCodeContextProvider_postFragment$data['items'][number]
}

type ShortCodeContextCemeteries = {
  [
    cemeteryId: number
  ]: ShortCodeContextProvider_cemeteryFragment$data['items'][number]
}

interface ShortCodeDataContextValue {
  posts: ShortCodeContextPosts | null
  cemeteries: ShortCodeContextCemeteries | null
}

export const ShortCodeDataContext = createContext<ShortCodeDataContextValue>({
  posts: null,
  cemeteries: null
})

interface ShortCodeContextProviderProps {
  postFragment: ShortCodeContextProvider_postFragment$key
  cemeteryFragment: ShortCodeContextProvider_cemeteryFragment$key
}
export const ShortCodeContextProvider: FC<
  PropsWithChildren<ShortCodeContextProviderProps>
> = ({ postFragment, cemeteryFragment, children }) => {
  const { items: postItems } = useFragment(postFragmentQuery, postFragment)
  const posts = useMemo(
    () =>
      postItems.reduce<ShortCodeContextPosts>((cur, item) => {
        return {
          ...cur,
          [item.slug]: item
        }
      }, {}),
    [postItems]
  )
  const { items: cemeteryItems } = useFragment(
    cemeteryFragmentQuery,
    cemeteryFragment
  )
  const cemeteries = useMemo(
    () =>
      cemeteryItems.reduce<ShortCodeContextCemeteries>((cur, item) => {
        return {
          ...cur,
          [item.cemeteryId]: item
        }
      }, {}),
    [cemeteryItems]
  )
  return (
    <ShortCodeDataContext.Provider value={{ posts, cemeteries }}>
      {children}
    </ShortCodeDataContext.Provider>
  )
}

const postFragmentQuery = graphql`
  fragment ShortCodeContextProvider_postFragment on PostResult {
    items {
      slug
      image {
        alt
        url
      }
      h1
      meta_description
    }
  }
`

const cemeteryFragmentQuery = graphql`
  fragment ShortCodeContextProvider_cemeteryFragment on CemeteryResult {
    items {
      cemeteryId: id
      name
      address
      accesses {
        car
        other
        shuttle_bus
        taxi
      }
      nearest_station {
        name
        distance
      }
      cemetery_types {
        name
        code
      }
      sects {
        name
        code
      }
      section_types {
        name
        code
      }
      review_summary {
        total_score
        count
        valuable_review {
          age
          answers {
            text
            code
          }
          gender
        }
      }
      main_image {
        alt
        active
        path
      }
      director_comment
      descriptions {
        subject
      }
      features {
        code
        name
      }
      facilities {
        code
        name
      }
      kana
      sub_images {
        path
        alt
      }
      best_price_by_category {
        normal {
          category_code
          price1
          price1_unit
          price2
          price2_unit
          purchase_price
          purchase_price_unit
          calculated_purchase_price
          category_code
          category_name
          purchase_price_option
          price3
          simulated_price3
          price4
          price4_name
          lot_area
        }
        nokotsu {
          category_code
          price1
          price1_unit
          price2
          price2_unit
          purchase_price
          purchase_price_unit
          calculated_purchase_price
          category_code
          category_name
          purchase_price_option
          price3
          simulated_price3
          price4
          price4_name
          lot_area
        }
        jumoku {
          category_code
          price1
          price1_unit
          price2
          price2_unit
          purchase_price
          purchase_price_unit
          calculated_purchase_price
          category_code
          category_name
          purchase_price_option
          price3
          simulated_price3
          price4
          price4_name
          lot_area
        }
        eitai_kuyo {
          category_code
          price1
          price1_unit
          price2
          price2_unit
          purchase_price
          purchase_price_unit
          calculated_purchase_price
          category_code
          category_name
          purchase_price_option
          price3
          simulated_price3
          price4
          price4_name
          lot_area
        }
      }
      prefecture {
        name
      }
      city {
        name
      }
      requestable
      price_and_specs {
        priceId: id
        name
        category_code
        price1
        price1_unit
        price2
        price2_unit
        purchase_price
        purchase_price_unit
        calculated_purchase_price
        price4
        price4_name
        price4_unit
        purchase_price_option
        price3
        price3_unit
        simulated_price3
        lot_area
        lot_area_spec
        capacity
        spec
        image {
          alt
          url
        }
      }
    }
  }
`
